import { create } from 'zustand';

export type SupportDrawerTab = 'my' | 'recent';

type SupportDrawerState = {
  /** "My tickets" slide-over (My / Recent tabs). */
  open: boolean;
  /** Report-ticket modal. */
  reportOpen: boolean;
  activeTab: SupportDrawerTab;
  /**
   * The ticket the drawer should open already expanded, if any. Set by deep
   * entry points (the notification bell), cleared once the row has consumed it
   * so a later manual open does not re-expand a stale ticket.
   */
  focusTicketId: string | null;
  openDrawer: () => void;
  /** Open the drawer on "My tickets" with one ticket already expanded. */
  openDrawerWithTicket: (ticketId: string) => void;
  clearFocusTicket: () => void;
  closeDrawer: () => void;
  /** Open the report modal directly. */
  openReport: () => void;
  closeReport: () => void;
  setTab: (tab: SupportDrawerTab) => void;
};

// Cross-page overlay state for the support drawer + report modal. Lives in
// the entities layer so both the widget (trigger/drawer) and the feature
// (report modal) can read it without a widget↔feature import cycle. Mirrors
// the incident-drawer store pattern.
export const useSupportDrawerStore = create<SupportDrawerState>()((set) => ({
  open: false,
  reportOpen: false,
  activeTab: 'my',
  focusTicketId: null,
  openDrawer: () => set({ open: true }),
  // Always lands on "My tickets": the only ticket event that deep-links here
  // is a comment on a ticket the signed-in user reported, which lives on that
  // tab by definition.
  openDrawerWithTicket: (ticketId) =>
    set({ open: true, activeTab: 'my', focusTicketId: ticketId }),
  clearFocusTicket: () => set({ focusTicketId: null }),
  closeDrawer: () => set({ open: false, focusTicketId: null }),
  openReport: () => set({ reportOpen: true }),
  closeReport: () => set({ reportOpen: false }),
  setTab: (tab) => set({ activeTab: tab }),
}));
